import {inject, Injectable, isDevMode, signal} from '@angular/core';
import {EThemeMode, TThemeMode} from '@shared/enums/theme.enum';
import {CookieStorageService} from '@shared/services/cookie-storage.service';
import {INavbarMenuItem} from '@shared/interfaces/navbar';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private cookieStorage = inject(CookieStorageService);
  private readonly STORE_KEY = 'ff_theme';

  readonly stateNavbarCollapsedSignal = signal<boolean>(true);

  private modeTheme: TThemeMode = EThemeMode.LIGHT;

  private themeIsNavbarVerticalCollapsedKeyStore = 'themeIsNavbarVerticalCollapsed';
  private themeIsNavbarVerticalCollapsed = false;

  readonly navbarMenu = signal<INavbarMenuItem[]>([]);

  constructor() {
    this.initialSetup();
  }

  private initialSetup() {
    const modeTheme = this.cookieStorage.get(this.STORE_KEY) || localStorage.getItem(this.STORE_KEY);

    if (modeTheme === EThemeMode.DARK) {
      this.modeTheme = EThemeMode.DARK;
    } else if (modeTheme === EThemeMode.AUTO) {
      this.modeTheme = this.getSystemTheme();
    }

    this.setDataBsTheme(this.modeTheme);

    this.setThemeIsNavbarVerticalCollapsed(localStorage.getItem(this.themeIsNavbarVerticalCollapsedKeyStore) == '1');
  }

  public getModeTheme(): TThemeMode {
    return this.modeTheme;
  }

  public isThemeDark() {
    return this.modeTheme === EThemeMode.DARK;
  }

  public setModeTheme(mode: TThemeMode) {
    this.modeTheme = mode;
    localStorage.setItem(this.STORE_KEY, mode);

    this.setDataBsTheme(mode);

    this.cookieStorage.set(this.STORE_KEY, mode, 3600 * 24 * 182, {
      domain: !isDevMode() ? '.fbfast.io' : '.fbfast-local.ua',
      sameSite: 'Lax',
      secure: true,
    });

    return this;
  }

  public getThemeIsNavbarVerticalCollapsed(): boolean {
    return this.themeIsNavbarVerticalCollapsed;
  }

  public setThemeIsNavbarVerticalCollapsed(mode: boolean) {
    this.themeIsNavbarVerticalCollapsed = mode;
    localStorage.setItem(this.themeIsNavbarVerticalCollapsedKeyStore, mode ? '1' : '0');

    if (mode) {
      document.documentElement.classList.add('navbar-vertical-collapsed');
    } else {
      document.documentElement.classList.remove('navbar-vertical-collapsed');
    }

    return this;
  }

  getData(el: HTMLElement, data: string) {
    const key: string = this.camelize(data);
    try {
      return JSON.parse(el.dataset[key] || '');
    } catch {
      return el.dataset[key];
    }
  }

  getSystemTheme(): TThemeMode {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? EThemeMode.DARK : EThemeMode.LIGHT;
  }

  getVarCssValue(varCssName: string) {
    return getComputedStyle(document.documentElement).getPropertyValue(varCssName).trim();
  }

  private camelize(str: string): string {
    const text = str.replace(/[-_\s.]+(.)?/g, (_, c) => (c ? c.toUpperCase() : ''));
    return `${text.substr(0, 1).toLowerCase()}${text.substr(1)}`;
  }

  private setDataBsTheme(mode: TThemeMode) {
    document.documentElement.setAttribute('data-bs-theme', mode);
  }
}
